/**
 * 017: structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/account-settings/personal-info.tsx (the `Form`
 * + `FormFooter` shell every FORM screen in this migration uses) and
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/category/create-category.tsx (the
 * page-with-back-button + FormGroup field layout).
 *
 * Only the data, columns and labels are ours. The screen-to-template mapping is
 * specs/017-isomorphic-theme-only/contracts/screen-map.md.
 */
import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { z } from "zod";
import toast from "react-hot-toast";
import { PiIdentificationBadgeDuotone as DetailsIcon, PiImageDuotone as ImageIcon, PiXBold as RemoveIcon } from "react-icons/pi";
import FormFooter from "@core/components/form-footer";
import Upload from "@core/ui/upload";
import { imageUrl, useFileUpload, type UploadedFile } from "@steelyard/common";
import { createBrand } from "../../../lib/services/catalog";
import { can } from "../../../lib/permissions";
import { formCard, formCardFooter, formSections } from "../../../lib/form-layout";
import { SectionTitle } from "../../../components/section-title";
import type { AccountSummary, PermissionState } from "../../../types";
import { PageHeader, ErrorSurface, problemTitle, Field, FormGroup, Form } from "@steelyard/theme";
import { ActionIcon, Text } from "rizzui";
import { UploadDropHint, UploadBusyHint } from "../../../components/upload-drop-hint";
import { useGoBack } from "../../../lib/navigation";

const brandSchema = z.object({
  name: z.string().trim().min(1, "Required").max(200),
  code: z.string().trim().min(1, "Required").max(50),
});
type BrandFormValues = z.infer<typeof brandSchema>;

/** Ported from apps/admin/app/(app)/catalogue/brands/new/page.tsx. */
export default function NewBrand() {
  const goBack = useGoBack("/catalogue/brands");
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [logoHash, setLogoHash] = useState<string | null>(null);
  const [uploadedLogo, setUploadedLogo] = useState<UploadedFile | null>(null);
  const [submitError, setSubmitError] = useState<unknown>(null);
  const logo = useFileUpload("image", {
    onUploaded: (result) => {
      setUploadedLogo(result);
      setLogoHash(result.hash);
      toast.success("Logo uploaded");
    },
    onError: () => toast.error("Could not upload the logo"),
  });

  if (!can(permissionState, "catalogue.administer")) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403 }} action="create a brand" />;
  }

  return (
    <div className="flex flex-col gap-6 @container">
      <PageHeader
        title="New Brand"
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Catalogue · Brands", href: "/catalogue/brands" }, { name: "New Brand" }]}
      />

      <Form<BrandFormValues>
        validationSchema={brandSchema}
        useFormProps={{ defaultValues: { name: "", code: "" } }}
        className={formCard}
        onSubmit={async (values) => {
          setSubmitError(null);
          try {
            await createBrand({ name: values.name, code: values.code, logoHash });
            toast.success("Brand created");
            goBack();
          } catch (err) {
            // The toast alone was missing on a fast rejection (a 409 duplicate code, most often —
            // it can resolve almost immediately) often enough that "no toast, nothing" was exactly
            // what a person saw, not just a toast that faded before it was noticed. This surfaces
            // the same failure as a message that stays on the page until the next submit.
            setSubmitError(err);
            toast.error(problemTitle(err, "Could not create the brand"));
          }
        }}
      >
        {({ register, formState: { errors, isSubmitting } }) => (
          <>
            <div className={formSections}>
              {submitError ? <ErrorSurface error={submitError} action="create this brand" /> : null}
              <FormGroup title={<SectionTitle icon={DetailsIcon}>Details</SectionTitle>} description="The code is permanent once saved — it is what appears on price lists and in Tally.">
                <Field label="Name" required maxLength={200} autoComplete="off" error={errors.name?.message} {...register("name")} />
                <Field
                  label="Code"
                  required
                  maxLength={50}
                  autoComplete="off"
                  className="font-mono"
                  hint="Unique across all brands, and not editable later."
                  error={errors.code?.message}
                  {...register("code")}
                />
              </FormGroup>
              <FormGroup title={<SectionTitle icon={ImageIcon}>Logo</SectionTitle>} description="Optional. Uploaded as a real file, not linked.">
                <div className="col-span-full flex flex-wrap items-center gap-4">
                  <Upload
                    accept="img"
                    multiple={false}
                    disabled={logo.busy}
                    onChange={logo.onChange}
                    aria-label="Brand logo file"
                    placeholderText={logo.busy ? <UploadBusyHint label="Uploading…" /> : <UploadDropHint />}
                    iconClassName="size-8 shrink-0"
                    className="p-4"
                    wrapperClassName="w-full max-w-xs"
                  />
                  {logoHash ? (
                    <div className="flex flex-col items-start gap-1.5">
                      <div className="relative">
                        <img src={imageUrl(logoHash)} alt="Uploaded brand logo" className="h-20 w-20 rounded-lg border border-muted object-cover" />
                        <ActionIcon
                          type="button"
                          size="sm"
                          variant="solid"
                          color="danger"
                          className="absolute -right-2 -top-2"
                          aria-label="Remove this logo"
                          onClick={() => {
                            setLogoHash(null);
                            setUploadedLogo(null);
                          }}
                        >
                          <RemoveIcon aria-hidden className="size-3.5" />
                        </ActionIcon>
                      </div>
                      {uploadedLogo ? (
                        <Text className="text-xs text-gray-500">
                          {uploadedLogo.sizeBytes.toLocaleString()} bytes · {uploadedLogo.contentType}
                        </Text>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                {logo.error ? <ErrorSurface error={logo.error} action="upload this logo" /> : null}
              </FormGroup>
            </div>

            <FormFooter
              className={formCardFooter}
              submitBtnText={isSubmitting ? "Creating…" : "Create brand"}
              isLoading={isSubmitting}
              altBtnText="Cancel"
              handleAltBtn={goBack}
            />
          </>
        )}
      </Form>
    </div>
  );
}
